import { API_BASE } from '../utils/config';
import { apiFetch } from '../utils/apiFetch';
import { Product, FilterParams, ProductPricing } from '../types';

const typeOf = (img: any): string | undefined =>
  (typeof img === 'object' && img !== null ? img.type : undefined);
const urlOf = (img: any): string | undefined =>
  (typeof img === 'string' ? img : img?.url);

const mapProduct = (p: any): Product => {
  const rawImages: any[] = p.image_uri || p.images || [];

  // The cutout made in the admin panel: the frame on transparency, for wearing on a face.
  // Kept apart from the rest because it is an overlay asset and not a catalogue photograph
  // — showing it in the gallery would put a checkerboard-backed PNG among the product shots.
  const tryonImageUrl = urlOf(rawImages.find((img: any) => typeOf(img) === 'tryon')) ?? null;
  const gallery = rawImages.filter((img: any) => typeOf(img) !== 'tryon');

  const sorted = [...gallery].sort((a: any, b: any) => {
    const aIsMain = typeOf(a) === 'main';
    const bIsMain = typeOf(b) === 'main';
    if (aIsMain && !bIsMain) return -1;
    if (!aIsMain && bIsMain) return 1;
    return 0;
  });
  const image_uri = sorted.length
    ? sorted.map(urlOf).filter(Boolean) as string[]
    : null;
  // Whether this product has a genuine front/main frame image (type === 'main').
  // Try-on requires a front shot — side/folded shots are not usable as an overlay.
  const hasMainImage = gallery.some((img: any) => typeOf(img) === 'main');
  return {
    ...p,
    selling_price: p.selling_price || p.price || p.unit_price || p.cost_price || 0,
    image_uri: image_uri && image_uri.length ? image_uri : null,
    hasMainImage,
    tryonImageUrl,
  };
};

export const getAllProducts = async (): Promise<Product[]> => {
  try {
    const res = await apiFetch(`${API_BASE}/products`);
    if (!res.ok) return [];
    const data = await res.json();
    return (data.products || []).map(mapProduct);
  } catch {
    return [];
  }
};

export const getFilteredProducts = async (filters: FilterParams): Promise<{ products: Product[]; hasMore: boolean }> => {
  try {
    const params: string[] = [];
    filters.categoryIds?.forEach((id) => params.push(`category_id=${id}`));
    filters.frameTypeIds?.forEach((id) => params.push(`frame_type_id=${id}`));
    filters.frameShapeIds?.forEach((id) => params.push(`frame_shape_id=${id}`));
    filters.idealFor?.forEach((id) => params.push(`ideal_for=${id}`));
    if (filters.productIds && filters.productIds.length > 0) {
      params.push(`product_ids=${filters.productIds.join(',')}`);
    }
    if (filters.minPrice != null) params.push(`min_price=${filters.minPrice}`);
    if (filters.maxPrice != null) params.push(`max_price=${filters.maxPrice}`);
    if (filters.sortBy) params.push(`sort=${filters.sortBy}`);
    if (filters.searchText) params.push(`searchText=${encodeURIComponent(filters.searchText)}`);
    params.push(`limit=${filters.limit ?? 20}`);
    params.push(`offset=${filters.offset ?? 0}`);

    const qs = `?${params.join('&')}`;
    const res = await apiFetch(`${API_BASE}/products/filter_products${qs}`);
    if (!res.ok) return { products: [], hasMore: false };
    const data = await res.json();
    return {
      products: (data.products || []).map(mapProduct),
      hasMore: data.has_more ?? false,
    };
  } catch {
    return { products: [], hasMore: false };
  }
};

export const getProductById = async (id: number): Promise<Product | null> => {
  try {
    const res = await apiFetch(`${API_BASE}/products/${id}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.product ? mapProduct(data.product) : null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------- details / pricing
//
// A product is fetched in two halves:
//
//   getProductDetails  what it IS (name, description, attributes, images). Rarely changes, so
//                      it is fetched once and kept for the session: reopening a product is instant.
//   getProductPricing  what it COSTS and how many are left. Never cached; fetched fresh every
//                      time a price is shown, so a shopper never sees an old price or stock.
//
// Both endpoints are public, so they use a bare fetch rather than apiFetch: with no
// Authorization or Content-Type header the browser skips the CORS preflight round trip, and it
// still sends Origin, which is all the API checks.

const detailsCache = new Map<number, Promise<Product | null>>();

/** A product's details, without price or stock. Cached for the session; null if missing. */
export const getProductDetails = (id: number): Promise<Product | null> => {
  const cached = detailsCache.get(id);
  if (cached) return cached;
  const promise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/products/${id}/details`);
      if (!res.ok) return null;
      const data = await res.json();
      return data.product ? mapProduct(data.product) : null;
    } catch {
      return null;
    }
  })();
  detailsCache.set(id, promise);
  // A miss or failure isn't worth remembering; the next visit asks again.
  promise.then((p) => { if (!p) detailsCache.delete(id); });
  return promise;
};

/** Live price and stock for one product. Null on any failure. */
export const getProductPricing = async (id: number, signal?: AbortSignal): Promise<ProductPricing | null> => {
  try {
    const res = await fetch(`${API_BASE}/products/${id}/pricing`, { cache: 'no-store', signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

/**
 * Live price and stock for many products in one call, keyed by product id. Null on any failure.
 * With no ids: every e-commerce or try-on product of the shop. With ids: just those (at most 200).
 */
export const getProductsPricing = async (ids?: number[]): Promise<Map<number, ProductPricing> | null> => {
  try {
    const qs = ids ? `?product_ids=${ids.join(',')}` : '';
    const res = await fetch(`${API_BASE}/products/pricing${qs}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return new Map((data.pricing as ProductPricing[]).map((p) => [p.product_id, p]));
  } catch {
    return null;
  }
};

/** The fields that change between visits: price, discount and stock. */
const COMMERCE_KEYS = ['selling_price', 'price', 'cost_price', 'discount', 'quantity'] as const;

const omitCommerce = <T extends object>(o: T): T => {
  const out: any = { ...o };
  for (const k of COMMERCE_KEYS) delete out[k];
  return out;
};

/**
 * A product with price, discount and stock taken out, colourways included: what may be cached.
 * Anything that forgets to ask for live pricing then shows no price rather than an old one.
 */
export const stripCommerce = (product: Product): Product => {
  const attr = product.product_attr;
  const colorways = (product as any).colorways;
  return {
    ...omitCommerce(product),
    product_attr: Array.isArray(attr?.frame_colors)
      ? { ...attr, frame_colors: attr.frame_colors.map(omitCommerce) }
      : attr,
    ...(Array.isArray(colorways) ? { colorways: colorways.map(omitCommerce) } : {}),
  } as Product;
};

/**
 * Lay live pricing over a product's details. Colourways are matched by stock_id; one the pricing
 * no longer lists has been removed, so it is marked out of stock.
 */
export const applyPricing = (product: Product, pricing: ProductPricing): Product => {
  const byStock = new Map(pricing.variants.map((v) => [v.stock_id, v]));
  const withLive = (c: any) => {
    const v = c.stock_id != null ? byStock.get(c.stock_id) : undefined;
    if (!v) return { ...c, quantity: 0 };
    return { ...c, price: v.selling_price, selling_price: v.selling_price, discount: v.discount, quantity: v.quantity };
  };
  const attr = product.product_attr;
  const colorways = (product as any).colorways;
  return {
    ...product,
    selling_price: pricing.selling_price,
    discount: pricing.discount,
    quantity: pricing.quantity,
    product_attr: Array.isArray(attr?.frame_colors)
      ? { ...attr, frame_colors: attr.frame_colors.map(withLive) }
      : attr,
    ...(Array.isArray(colorways) ? { colorways: colorways.map(withLive) } : {}),
  };
};
